import axios from 'axios';
import { APP_TIMEZONE } from '../utils/dayjsConfig';

const UNOFFICIAL_API_URL = process.env.REACT_APP_UNOFFICIAL_API_URL || 'https://whatsapp.ligchat.com/api/v1';

export interface UnofficialMessageResponse {
  status: string;
  message: string;
  /** Resposta completa do Go em send-message (id real, content com prefixo, etc.) */
  data: {
    recipient?: string;
    message?: string;
    id?: number;
    content?: string;
    mediaType?: string;
    mediaUrl?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    sectorId?: number;
    contactID?: number;
    sentAt?: string;
    isSent?: boolean;
    isRead?: boolean;
    whatsappMessageID?: string;
    replyToMessageID?: string;
    repliedMessage?: any;
    messageStatus?: string;
  };
  timestamp: string;
}

export interface SendTextMessageDTO {
  sector_id: number;
  recipient: string;
  message: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  replyToMessageId?: string; // ID da mensagem respondida no WhatsApp
}

export interface SendImageDTO {
  base64File: string;
  fileName: string;
  mediaType: string;
  recipient: string;
  sectorId: number;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  replyToMessageId?: string; // ID da mensagem respondida no WhatsApp
}

export interface SendDocumentDTO {
  base64File: string;
  fileName: string;
  mediaType: string;
  recipient: string;
  sectorId: number;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  replyToMessageId?: string; // ID da mensagem respondida no WhatsApp
}

export interface SendMediaMessageDTO {
  base64File: string;
  fileName: string;
  mediaType: string;
  recipient: string;
  sectorId: number;
  caption?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  replyToMessageId?: string; // ID da mensagem respondida no WhatsApp
}

export const sendTextMessage = async (data: SendTextMessageDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      ...data,
      sentAt: data.sentAt || new Date().toISOString(),
      timezone: data.timezone || APP_TIMEZONE
    };

    console.log('Payload sendo enviado pelo UnofficialMessageService sendTextMessage:', payload);

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-message`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface SendInteractiveReplyDTO {
  sector_id: number;
  recipient: string;
  chat_jid?: string;
  userId?: number;
  isAnonymous?: boolean;
  stanza_id: string;
  kind: string;
  selected_button_id?: string;
  selected_display_text?: string;
  list_title?: string;
  list_description?: string;
  list_row_id?: string;
  native_flow_name?: string;
  native_flow_params_json?: string;
  native_flow_version?: number;
}

export const sendInteractiveReply = async (data: SendInteractiveReplyDTO): Promise<{ status?: string }> => {
  const response = await axios.post<{ status?: string }>(
    `${UNOFFICIAL_API_URL}/send-interactive-reply`,
    data,
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

export const sendImage = async (data: SendImageDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      base64File: data.base64File,
      fileName: data.fileName,
      mediaType: data.mediaType,
      recipient: data.recipient,
      sectorId: data.sectorId,
      caption: data.caption || '',
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString(),
      timezone: data.timezone || APP_TIMEZONE,
      replyToMessageId: data.replyToMessageId
    };

    console.log('Payload sendo enviado pelo UnofficialMessageService sendImage:', payload);

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-image`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendAudio = async (data: SendMediaMessageDTO): Promise<UnofficialMessageResponse> => {
  try {
    // Garantir que o base64 não tenha o prefixo data:audio
    const base64File = data.base64File.replace(/^data:audio\/(wav|mpeg|mp3|ogg);base64,/, '');

    const payload = {
      base64File,
      fileName: data.fileName,
      mediaType: 'audio/wav', // Forçar o tipo como WAV
      recipient: data.recipient,
      sectorId: data.sectorId,
      caption: data.caption || '',
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString(),
      timezone: data.timezone || APP_TIMEZONE,
      replyToMessageId: data.replyToMessageId
    };

    console.log('Payload sendo enviado pelo UnofficialMessageService sendAudio:', payload);

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-audio`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendDocument = async (data: SendDocumentDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      base64File: data.base64File,
      fileName: data.fileName,
      mediaType: data.mediaType,
      recipient: data.recipient,
      sectorId: data.sectorId,
      caption: data.caption || '',
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString(),
      timezone: data.timezone || APP_TIMEZONE,
      replyToMessageId: data.replyToMessageId
    };

    console.log('Payload sendo enviado pelo UnofficialMessageService sendDocument:', payload);

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-document`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Função auxiliar para converter File/Blob para Base64
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      }
    };
    reader.onerror = error => reject(error);
  });
};


export interface SendContactDTO {
  recipient: string;
  sectorId: number;
  contactName: string;
  contactPhone: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
}

export const sendContact = async (data: SendContactDTO): Promise<UnofficialMessageResponse> => {
  try {
    const payload = {
      recipient: data.recipient,
      sector_id: data.sectorId,
      contact_name: data.contactName,
      contact_phone: data.contactPhone,
      userId: data.userId,
      isAnonymous: data.isAnonymous,
      sentAt: data.sentAt || new Date().toISOString(),
      timezone: data.timezone || APP_TIMEZONE
    };

    console.log('Payload sendo enviado pelo UnofficialMessageService sendContact:', payload);

    const response = await axios.post<UnofficialMessageResponse>(`${UNOFFICIAL_API_URL}/send-contact`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao enviar contato via WhatsApp:', error);
    throw error;
  }
};

export interface SendReactionDTO {
  sectorId: number;
  contactId: number;
  whatsappMessageId: string;
  /** true se a mensagem alvo foi enviada por nós (balão verde) */
  targetFromMe: boolean;
  /** vazio = remover reação */
  emoji: string;
}

export const sendReaction = async (data: SendReactionDTO): Promise<{ status?: string; message?: string }> => {
  const response = await axios.post(`${UNOFFICIAL_API_URL}/send-reaction`, {
    sectorId: data.sectorId,
    contactId: data.contactId,
    whatsappMessageId: data.whatsappMessageId,
    targetFromMe: data.targetFromMe,
    emoji: data.emoji
  }, {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
};
